import { Alert } from "@package/subframe/components/Alert";
import { Select } from "@package/subframe/components/Select";
import { TextField } from "@package/subframe/components/TextField";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { toast } from "sonner";

import type { EmployeeKeyInformationDto } from "@/generated/model/employeeKeyInformationDto";
import { EmploymentStatus } from "@/generated/model/employmentStatus";
import {
  useListLineManagerCandidates,
  useUpdateEmployeeKeyInformation,
} from "@/generated/people/people";
import { useListRoomsQueryControllerList } from "@/generated/rooms/rooms";
import { useIsEmployeePhotoChanging } from "@/hooks/use-employee-photo";
import { useInvalidateEmployeeProfile } from "@/hooks/use-invalidate-employee-profile";
import { extractApiErrorMessage } from "@/lib/compliance/invalidate-compliance-queries";
import { employmentStatusLabel } from "@/lib/people";
import {
  type KeyInformationFormValues,
  keyInformationFormValues,
  keyInformationRequest,
  keyInformationSchema,
} from "@/lib/people-key-information";

import { ProfileSectionForm } from "../ProfileSectionForm";

/** Radix Select items can't have an empty value, so "none" needs a stand-in. */
const NONE = "__none__";

type TextFieldKey =
  | "firstName"
  | "lastName"
  | "preferredName"
  | "pronouns"
  | "employeeId"
  | "jobTitle";

const TEXT_FIELDS: { key: TextFieldKey; label: string }[] = [
  { key: "firstName", label: "First name" },
  { key: "lastName", label: "Last name" },
  { key: "preferredName", label: "Preferred name" },
  { key: "pronouns", label: "Pronouns" },
  { key: "employeeId", label: "Employee ID" },
  { key: "jobTitle", label: "Job title" },
];

interface EditKeyInformationFormProps {
  nurseryId: string;
  userId: string;
  keyInformation: EmployeeKeyInformationDto;
  onDone: () => void;
}

/**
 * The form only exists while it is open, so it starts from the saved profile
 * and a photo change, which refetches the profile, can't wipe unsaved edits.
 */
export function EditKeyInformationForm({
  nurseryId,
  userId,
  keyInformation,
  onDone,
}: EditKeyInformationFormProps) {
  const [values, setValues] = useState<KeyInformationFormValues>(() =>
    keyInformationFormValues(keyInformation),
  );
  const [errors, setErrors] = useState<Record<string, string>>({});
  const photoChanging = useIsEmployeePhotoChanging(userId);
  const invalidateProfile = useInvalidateEmployeeProfile(nurseryId, userId);
  const queryClient = useQueryClient();

  const roomsQuery = useListRoomsQueryControllerList(nurseryId);
  const candidatesQuery = useListLineManagerCandidates(nurseryId, userId);

  const update = useUpdateEmployeeKeyInformation({
    mutation: {
      onSuccess: async () => {
        await invalidateProfile();
        // The name and room also show in the staff lists.
        await queryClient.invalidateQueries({
          predicate: (query) =>
            typeof query.queryKey[0] === "string" &&
            (query.queryKey[0].includes("/members") ||
              query.queryKey[0].includes("/users")),
        });
        toast.success("Key information saved");
        onDone();
      },
      onError: (error: unknown) => {
        const message = extractApiErrorMessage(
          error,
          "Could not save key information",
        );
        if (statusOf(error) === 409) {
          setErrors({ employeeId: message });
          return;
        }
        toast.error(message);
      },
    },
  });

  // The person's current room or line manager may no longer be offered (an
  // inactive room, a manager who has left): keep them selectable so saving an
  // unrelated change doesn't clear them. Marked as such only once the list has
  // loaded, so a room isn't briefly shown as inactive.
  const roomOptions = withCurrent(
    (roomsQuery.data?.data ?? []).map((room) => ({
      value: room.id,
      label: room.name,
    })),
    keyInformation.room && {
      value: keyInformation.room.id,
      label: roomsQuery.isSuccess
        ? `${keyInformation.room.name} (inactive)`
        : keyInformation.room.name,
    },
  );
  const lineManagerOptions = withCurrent(
    (candidatesQuery.data?.data ?? []).map((candidate) => ({
      value: candidate.userId,
      label: candidate.name ?? candidate.email,
    })),
    keyInformation.lineManager && {
      value: keyInformation.lineManager.userId,
      label: keyInformation.lineManager.name ?? "Former line manager",
    },
  );
  const listsFailed = roomsQuery.isError || candidatesQuery.isError;

  const setField = <K extends keyof KeyInformationFormValues>(
    key: K,
    value: KeyInformationFormValues[K],
  ) => setValues((current) => ({ ...current, [key]: value }));

  const handleSubmit = () => {
    const result = keyInformationSchema.safeParse(values);
    if (!result.success) {
      const fieldErrors: Record<string, string> = {};
      result.error.issues.forEach((issue) => {
        fieldErrors[issue.path.join(".")] ??= issue.message;
      });
      setErrors(fieldErrors);
      return;
    }

    setErrors({});
    update.mutate({ nurseryId, userId, data: keyInformationRequest(values) });
  };

  const isPending = update.isPending;

  return (
    <ProfileSectionForm
      testId="edit-key-information-form"
      isPending={isPending}
      onCancel={onDone}
      onSubmit={handleSubmit}
      saveDisabled={photoChanging}
    >
      {listsFailed && (
        <Alert
          variant="error"
          title="Couldn't load the rooms or line managers"
          description="Cancel, then press Edit again to retry."
        />
      )}

      <div className="grid w-full grid-cols-1 gap-6 @sm:grid-cols-2">
        {TEXT_FIELDS.map(({ key, label }) => (
          <TextField
            key={key}
            className="h-auto w-full"
            size="large"
            label={label}
            error={!!errors[key]}
            helpText={errors[key]}
          >
            <TextField.Input
              name={key}
              value={values[key]}
              onChange={(event) => setField(key, event.target.value)}
              disabled={isPending}
            />
          </TextField>
        ))}

        <Select
          label="Team / room"
          value={values.roomId || NONE}
          onValueChange={(value) =>
            setField("roomId", value === NONE ? "" : value)
          }
          disabled={isPending}
          error={!!errors.roomId}
          helpText={errors.roomId}
        >
          <Select.Item value={NONE}>No room</Select.Item>
          {roomOptions.map((option) => (
            <Select.Item key={option.value} value={option.value}>
              {option.label}
            </Select.Item>
          ))}
        </Select>

        <Select
          label="Line manager"
          value={values.lineManagerUserId || NONE}
          onValueChange={(value) =>
            setField("lineManagerUserId", value === NONE ? "" : value)
          }
          disabled={isPending}
          error={!!errors.lineManagerUserId}
          helpText={
            errors.lineManagerUserId ??
            "Managers and deputy managers at this nursery."
          }
        >
          <Select.Item value={NONE}>No line manager</Select.Item>
          {lineManagerOptions.map((option) => (
            <Select.Item key={option.value} value={option.value}>
              {option.label}
            </Select.Item>
          ))}
        </Select>

        <TextField
          className="h-auto w-full"
          size="large"
          label="Start date"
          error={!!errors.startDate}
          helpText={errors.startDate}
        >
          <TextField.Input
            type="date"
            name="startDate"
            value={values.startDate}
            onChange={(event) => setField("startDate", event.target.value)}
            disabled={isPending}
          />
        </TextField>

        <TextField
          className="h-auto w-full"
          size="large"
          label="End date"
          error={!!errors.endDate}
          helpText={errors.endDate ?? "Required when the status is Left."}
        >
          <TextField.Input
            type="date"
            name="endDate"
            value={values.endDate}
            onChange={(event) => setField("endDate", event.target.value)}
            disabled={isPending}
          />
        </TextField>

        <Select
          label="Employment status"
          value={values.employmentStatus || NONE}
          onValueChange={(value) =>
            setField(
              "employmentStatus",
              value === NONE ? "" : (value as EmploymentStatus),
            )
          }
          disabled={isPending}
        >
          <Select.Item value={NONE}>Not recorded</Select.Item>
          {Object.values(EmploymentStatus).map((status) => (
            <Select.Item key={status} value={status}>
              {employmentStatusLabel(status)}
            </Select.Item>
          ))}
        </Select>
      </div>
    </ProfileSectionForm>
  );
}

interface Option {
  value: string;
  label: string;
}

function withCurrent(
  options: Option[],
  current: Option | null | undefined,
): Option[] {
  if (!current) return options;
  if (options.some((option) => option.value === current.value)) {
    return options;
  }
  return [...options, current];
}

function statusOf(error: unknown): number | undefined {
  return (error as { response?: { status?: number } })?.response?.status;
}
